import { logStartButtonState, logAssetLoadingTiming } from "@/Utils/logger";

export interface StartButtonState {
  cameraPermitted: boolean;
  motionPermitted: boolean;
  allImagesLoaded: boolean;
  allAudiosLoaded: boolean;
  modelsLoading: boolean;
  isPortrait: boolean;
  isRetryingAssets: boolean;
  scanningState: string;
  isStartButtonEnabled: boolean;
}

export class StartButtonMonitor {
  private static instance: StartButtonMonitor;
  private navigationStartTime: number | null = null;
  private startButtonDisabledTime: number | null = null;
  private monitoringInterval: NodeJS.Timeout | null = null;
  private lastState: StartButtonState | null = null;
  private assetLoadingStartTimes: Map<string, number> = new Map();

  private constructor() {}

  public static getInstance(): StartButtonMonitor {
    if (!StartButtonMonitor.instance) {
      StartButtonMonitor.instance = new StartButtonMonitor();
    }
    return StartButtonMonitor.instance;
  }

  /**
   * Called when user navigates from tutorial to scanning page
   */
  public onNavigationToScanning(): void {
    this.navigationStartTime = Date.now();
    this.startButtonDisabledTime = Date.now();
    
    logStartButtonState("Navigation to scanning page started", {
      timeSinceNavigationMs: 0,
      scanningState: "moveback"
    });

    this.startPeriodicMonitoring();
  }

  /**
   * Called when Start button state changes
   */
  public onStartButtonStateChange(state: StartButtonState): void {
    const now = Date.now();
    const timeSinceNavigation = this.navigationStartTime ? now - this.navigationStartTime : 0;
    
    // Handle initial state (first time we see the state)
    if (!this.lastState) {
      if (!state.isStartButtonEnabled) {
        this.startButtonDisabledTime = now;
        logStartButtonState("Initial state: Start button disabled", {
          ...state,
          timeSinceNavigationMs: timeSinceNavigation,
          timeSinceDisabledMs: 0
        });
        this.startPeriodicMonitoring();
      } else {
        logStartButtonState("Initial state: Start button enabled", {
          ...state,
          timeSinceNavigationMs: timeSinceNavigation,
          timeSinceDisabledMs: 0
        });
      }
    }
    // Check if Start button became enabled
    else if (state.isStartButtonEnabled && !this.lastState.isStartButtonEnabled) {
      const timeSinceDisabled = this.startButtonDisabledTime ? now - this.startButtonDisabledTime : 0;
      
      logStartButtonState("Start button enabled", {
        ...state,
        timeSinceNavigationMs: timeSinceNavigation,
        timeSinceDisabledMs: timeSinceDisabled
      });

      this.stopPeriodicMonitoring();
      this.startButtonDisabledTime = null;
    }
    // Check if Start button became disabled
    else if (!state.isStartButtonEnabled && this.lastState.isStartButtonEnabled) {
      this.startButtonDisabledTime = now;
      
      logStartButtonState("Start button disabled", {
        ...state,
        timeSinceNavigationMs: timeSinceNavigation,
        timeSinceDisabledMs: 0
      });

      this.startPeriodicMonitoring();
    }

    this.lastState = { ...state };
  }

  /**
   * Called when asset loading starts
   */
  public onAssetLoadingStart(assetType: "images" | "audios" | "models"): void {
    const now = Date.now();
    this.assetLoadingStartTimes.set(assetType, now);
    
    logAssetLoadingTiming(assetType, "start", {
      navigationTime: this.navigationStartTime ? now - this.navigationStartTime : 0
    });
  }

  /**
   * Called when asset loading completes
   */
  public onAssetLoadingComplete(assetType: "images" | "audios" | "models"): void {
    const now = Date.now();
    const startTime = this.assetLoadingStartTimes.get(assetType);
    const loadingDuration = startTime ? now - startTime : 0;
    const navigationTime = this.navigationStartTime ? now - this.navigationStartTime : 0;
    
    logAssetLoadingTiming(assetType, "complete", {
      loadingDurationMs: loadingDuration,
      navigationTime: navigationTime
    });

    this.assetLoadingStartTimes.delete(assetType);
  }

  /**
   * Called when asset loading fails
   */
  public onAssetLoadingFailed(assetType: "images" | "audios" | "models", error?: any): void {
    const now = Date.now();
    const startTime = this.assetLoadingStartTimes.get(assetType);
    const loadingDuration = startTime ? now - startTime : 0;
    const navigationTime = this.navigationStartTime ? now - this.navigationStartTime : 0;
    
    logAssetLoadingTiming(assetType, "failed", {
      loadingDurationMs: loadingDuration,
      navigationTime: navigationTime,
      error: error?.message || "Unknown error"
    });
  }

  /**
   * Called when asset loading retries
   */
  public onAssetLoadingRetry(assetType: "images" | "audios" | "models", retryCount: number): void {
    const now = Date.now();
    const navigationTime = this.navigationStartTime ? now - this.navigationStartTime : 0;
    
    logAssetLoadingTiming(assetType, "retry", {
      retryCount,
      navigationTime: navigationTime
    });
  }

  /**
   * Start periodic monitoring every 10 seconds
   */
  private startPeriodicMonitoring(): void {
    if (this.monitoringInterval) {
      return; // Already monitoring
    }

    this.monitoringInterval = setInterval(() => {
      if (this.lastState && !this.lastState.isStartButtonEnabled) {
        const now = Date.now();
        const timeSinceNavigation = this.navigationStartTime ? now - this.navigationStartTime : 0;
        const timeSinceDisabled = this.startButtonDisabledTime ? now - this.startButtonDisabledTime : 0;

        logStartButtonState(`Start button disabled for ${Math.round(timeSinceDisabled / 1000)}s`, {
          ...this.lastState,
          timeSinceNavigationMs: timeSinceNavigation,
          timeSinceDisabledMs: timeSinceDisabled
        });
      }
    }, 10000); // Log every 10 seconds
  }

  /**
   * Stop periodic monitoring
   */
  private stopPeriodicMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
  }

  /**
   * Reset monitoring state (called when leaving scanning flow)
   */
  public reset(): void {
    this.stopPeriodicMonitoring();
    this.navigationStartTime = null;
    this.startButtonDisabledTime = null;
    this.lastState = null;
    this.assetLoadingStartTimes.clear();
  }

  /**
   * Get current monitoring state for debugging
   */
  public getState(): {
    navigationStartTime: number | null;
    startButtonDisabledTime: number | null;
    isMonitoring: boolean;
    lastState: StartButtonState | null;
  } {
    return {
      navigationStartTime: this.navigationStartTime,
      startButtonDisabledTime: this.startButtonDisabledTime,
      isMonitoring: this.monitoringInterval !== null,
      lastState: this.lastState,
    };
  }
}
